/* eslint-disable @repo/no-style-props */
import {
  OpenAIContentParts,
  OpenAIContentSchema,
  type OpenAIOutputAudioType,
} from "@langfuse/shared";
import { Button } from "@/src/components/ui/button";
import { PrettyJsonView } from "@/src/components/ui/PrettyJsonView";
import { MarkdownView } from "@/src/components/ui/MarkdownViewer";
import { type MediaReturnType } from "@/src/features/media";
import { Check, Copy } from "lucide-react";
import { useMemo, useState } from "react";
import { type z } from "zod";
import { useMarkdownRenderCharacterLimit } from "@/src/hooks/useMarkdownRenderCharacterLimit";
import { cn } from "@/src/utils/tailwind";
import { classifyMediaValue } from "@/src/components/ui/media/mediaUtils";

type MarkdownJsonViewHeaderProps = {
  title: string | React.ReactNode;
  titleIcon?: React.ReactNode;
  handleOnCopy: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  controlButtons?: React.ReactNode;
  /** Hosts that render their own copy control (e.g. inside the content box)
      suppress the header's. */
  hideCopyButton?: boolean;
  /** Reveal the right-side controls only while hovering the hosting section
      (requires a `group/iosection` ancestor). */
  hoverRevealControls?: boolean;
};

export function MarkdownJsonViewHeader({
  title,
  titleIcon,
  handleOnCopy,
  controlButtons,
  hideCopyButton = false,
  hoverRevealControls = false,
}: MarkdownJsonViewHeaderProps) {
  const [isCopied, setIsCopied] = useState(false);

  const titleContent = (
    <>
      {titleIcon}
      {title}
    </>
  );

  return (
    <div className="io-message-header flex flex-row items-center justify-between px-1 py-1 text-base font-bold capitalize">
      {/* Masked from session recordings: the title can be a customer-provided
          message `name` (or tool name) rather than a fixed role string. */}
      <div className="ph-no-capture flex items-center gap-2">
        {titleContent}
      </div>
      <div
        className={cn(
          "mr-1 flex min-w-0 shrink flex-row items-center gap-1",
          // pointer-coarse: touch devices have no hover, so the controls
          // stay visible there instead of being unreachable.
          hoverRevealControls &&
            "opacity-0 transition-opacity group-hover/iosection:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100",
        )}
      >
        {controlButtons}
        {!hideCopyButton && (
          <Button
            title="Copy to clipboard"
            variant="ghost"
            size="icon-xs"
            type="button"
            onClick={(event) => {
              setIsCopied(true);
              handleOnCopy(event);
              setTimeout(() => setIsCopied(false), 1000);
            }}
            className="text-muted-foreground hover:text-foreground hover:bg-transparent"
          >
            {isCopied ? (
              <Check className="icon-sm text-icon-foreground" />
            ) : (
              <Copy className="icon-sm text-icon-foreground" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Whether `content` renders through MarkdownView (inline text/image/audio
 * parts) rather than as a JSON table. Narrows to the schema's INPUT type:
 * `MediaReferenceStringSchema` transforms `@@@langfuseMedia:…@@@` into a parsed
 * object, and the renderer's own part guards re-validate the untransformed
 * shape — so the raw content is what must reach it (LFE-14815).
 *
 * Exported so callers deduping the media strip against what rendered inline
 * ask the same question the renderer answered.
 */
export const canRenderContentAsMarkdown = (
  content: unknown,
  characterLimit: number,
): content is z.input<typeof OpenAIContentSchema> =>
  (OpenAIContentSchema.safeParse(content).success ||
    isOpenAIContentWithExternalS3Image(content)) &&
  // Don't render if markdown content is huge
  JSON.stringify(content || {}).length <= characterLimit;

const isOpenAIContentWithExternalS3Image = (content: unknown) => {
  if (!Array.isArray(content)) return false;

  let hasExternalS3Image = false;
  const hasOnlyRenderableParts = content.every((part) => {
    if (
      typeof part === "object" &&
      part !== null &&
      part.type === "image_url" &&
      typeof part.image_url === "object" &&
      part.image_url !== null
    ) {
      const descriptor = classifyMediaValue(part.image_url.url);
      if (descriptor?.kind === "s3") {
        hasExternalS3Image = true;
        return true;
      }
    }

    return OpenAIContentParts.safeParse([part]).success;
  });

  return hasExternalS3Image && hasOnlyRenderableParts;
};

// MarkdownJsonView renders markdown whenever the content is valid markdown
// (see canRenderContentAsMarkdown), otherwise it falls back to JSON.
export function MarkdownJsonView({
  content,
  title,
  titleIcon,
  className,
  audio,
  media,
  controlButtons,
  afterHeader,
  isSystemPrompt,
}: {
  content?: unknown;
  title?: string;
  titleIcon?: React.ReactNode;
  className?: string;
  audio?: OpenAIOutputAudioType;
  media?: MediaReturnType[];
  controlButtons?: React.ReactNode;
  /** Content to render between header and main content (e.g., thinking blocks) */
  afterHeader?: React.ReactNode;
  /** Collapse long content to a preview (from raw `role === "system"`, since
      the title can carry a message `name` instead of the role). */
  isSystemPrompt?: boolean;
}) {
  const characterLimit = useMarkdownRenderCharacterLimit();
  // Boxed so a renderable `null` content stays distinguishable from
  // "not renderable as markdown", without re-widening the narrowed type.
  const markdownContent = useMemo(
    () =>
      canRenderContentAsMarkdown(content, characterLimit)
        ? { value: content }
        : null,
    [content, characterLimit],
  );

  return (
    <>
      {markdownContent ? (
        <MarkdownView
          markdown={markdownContent.value}
          title={title}
          titleIcon={titleIcon}
          audio={audio}
          media={media}
          controlButtons={controlButtons}
          afterHeader={afterHeader}
          isSystemPrompt={isSystemPrompt}
        />
      ) : (
        <PrettyJsonView
          json={content ?? (audio ? { audio } : null)}
          title={title}
          titleIcon={titleIcon}
          className={className}
          media={media}
          currentView="pretty"
          controlButtons={controlButtons}
          afterHeader={afterHeader}
          isSystemPrompt={isSystemPrompt}
        />
      )}
    </>
  );
}
